import { getDateDifferenceInText } from "@/composables/dateHandler";
import Category from "./Category";
import BaseModel from "./BaseModel";
import imageHandler from "@/composables/handlers/ImageHandler";
import LanguageService from "@/services/LanguageService";

/**
 * Course
 */
export default class Course extends BaseModel{
  courseStatus = {
    COMPLETE: 'complete',
    InCOMPLETE: 'incomplete'
  }

  constructor({
    id,
    title,
    description,
    timeInMinutes,
    courseLanguage,
    teachedLanguage,
    coursePhoto,
    author,
    uuid,
    price,
    status,
    created_at,
    updated_at,
    deleted_at,
    categories,
    resoursesCount,
    hours,
    videosNumber,
    lessonsCount,
    reviewsCount,
    authorName,
    rating,
    buys_number,
    active,
    is_blocked
  }) {
    super();

    this.id = id || null;
    this.title = title || null;
    this.description = description || null;
    this.timeInMinutes = timeInMinutes || null;
    this.courseLanguage = courseLanguage || null;
    this.teachedLanguage = teachedLanguage || null;
    this.coursePhoto = coursePhoto || null;
    this.author = author || null;
    this.uuid = uuid || null;
    this.price = price || null;
    this.status = status || null;
    this.created_at = created_at || null;
    this.updated_at = updated_at || null;
    this.deleted_at = deleted_at || null;
    this.last_update = this.updated_at != null ? getDateDifferenceInText(this.updated_at) : null;
    this.categories = categories || null;
    this.resoursesCount = resoursesCount || 0;
    this.hours = hours || null;
    this.videosNumber = videosNumber || 0;
    this.lessonsCount = lessonsCount || 0;
    this.reviewsCount = reviewsCount || 0;
    this.authorName = authorName || null;
    this.rating = rating || null;
    this.buys_number = buys_number || 0;
    this.active = Number.isInteger(active) ? active : 1;
    this.is_blocked = is_blocked || false;

    if (this.categories)
      this.handleCategories();

    this.handleLanguages();

    this.VALID_SORTS_ON_SEARCH = [
      { label: 'most relevant', value: 'relevance' },
      { label: 'newest', value: 'newest' },
      { label: 'cheaper', value: 'most-cheaper' },
      { label: 'most expensive', value: 'most-expensive' }
    ];
  }

  /**
   * Checks if course is completed
   * @returns {Boolean}
   */
  isCompleted() {
    return this.status == this.courseStatus.COMPLETE ? true : false;
  }

  /**
   * Checks if course is blocked
   * @returns {Boolean}
   */
  isBlocked() {
    return this.is_blocked ? true : false;
  }

  /**
   * Adds <img> src necessary tags
   * @param {String} coursePhoto 
   * @returns {String}
   */
  getCoursePhoto() {
    let photo = imageHandler.defaultIcon();
    if (this.coursePhoto && typeof this.coursePhoto == 'string') {
      photo = this.coursePhoto.indexOf('data:image/png;base64,') == 0 ? this.coursePhoto : `data:image/png;base64,${this.coursePhoto}`;
    }
    return photo;
  }

  /**
   * Get courseStatus values
   * @returns {Object}
   */
  static getStatus() {
    return this.courseStatus;
  }

  /**
   * Parses this.categories to Course objects array
   */
  handleCategories() {
    if (Array.isArray(this.categories)) {

      let categoriesArray = [];
      for (let cat of this.categories) {
        categoriesArray.push(new Category(cat));
      }

      this.categories = categoriesArray;
    }
  }

  /**
   * Checks if this Course has Category object array
   * @returns {boolean}
   */
  hasCategories() {
    return Array.isArray(this.categories) && this.categories.length > 0 ? true : false;
  }

  /**
   * Sets languages objects to this course
   * @returns 
   */
  async handleLanguages() {
    let languages = LanguageService.getLanguages();

    if (Array.isArray(languages) && languages.length < 1)
      return;

    if (this.courseLanguage && languages.get(this.courseLanguage)) {
      this.courseLanguage = languages.get(this.courseLanguage).getValue();
    }

    if (this.teachedLanguage && languages.get(this.teachedLanguage)) {
      this.teachedLanguage = languages.get(this.teachedLanguage).getValue();
    }
  }

  /**
   * Gets rating rounded
   */
  getRating() {
    return parseInt(this.rating) || 0;
  }

  /**
   * Gets buys number
   * @param {boolean} addPlus - to add a '+' character when higher than one thousand 
   */
  getBuysNumber(addPlus = false) {
    return addPlus && this.buys_number > 1000 ? `+ ${this.buys_number}` : this.buys_number;
  }

  /**
   * Get course title, uses '...' at the end if name is too long
   * @param {integer} acceptedLength
   */
  getEnshortedTitle(acceptedLength = 25) {
    return `${this.title.slice(0, acceptedLength)} ${this.title.length > acceptedLength ? '...' : ''}`;
  }

  /**
   * Gets Course statistics data
   * @returns {Object}
   */
  getStatisticData() {
    return {
      resoursesCount: this.resoursesCount,
      videosNumber: this.videosNumber,
      lessonsCount: this.lessonsCount,
      reviewsCount: this.reviewsCount
    }
  }

  /**
   * Gets timeInMinutes as number
   * @returns {Number}
   */
  getTimeInMinutes() {
    return Math.round(parseInt(this.timeInMinutes));
  }

  /**
   * Gets course timeInMinutes parsed as hours
   * @returns {string}
   */
  getCourseTimeInHours() {
    const minutes = parseInt(this.timeInMinutes || 0);

    if (minutes < 60) {
      const hours = minutes / 60;
      return hours.toFixed(2).replace('.', ',');
    }

    const value = minutes / 60;
    return value.toFixed(2).replace('.', ',');
  }
}